import {Component, ElementRef, OnInit, ViewChild, ChangeDetectorRef} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {FixedAssetReportsApiService, RegisterFilters, RegisterRowItem} from '../../../../Services/fixed-asset-reports-api.service';
import {FixedAssetListItemModel} from '../../../../DataModels/fixed-asset-model';
import {DataExportService} from '../../../../Services/dataExport.service';
import {NotificationService} from '../../../../Services/notification.service';
import {EmailModel} from '../../../../DataModels/email-model';
import {Modal} from 'bootstrap';
import {DateFilterService} from '../../../../Services/dateFilter.service';
import {DataService} from '../../../../Services/data.service';
import {ResponseModel} from '../../../../DataModels/response-model';
import {of} from 'rxjs';
import {ReportPrintService} from '../../../../Services/report-print.service';

@Component({
  selector: 'app-asset-register-report',
  templateUrl: './asset-register.component.html',
  styleUrls: ['./asset-register.component.css'],
  standalone: false
})
export class AssetRegisterComponent implements OnInit {
  list: (FixedAssetListItemModel & { accumBookAsOfPost?: number | null; accumMacrsAsOfPost?: number | null })[] = [];
  // Precompute localized labels for template usage
  yesLabel: string = $localize`:Yes@@yes:Yes`;
  noLabel: string = $localize`:No@@no:No`;
  // View model required by shared report header buttons
  vm: any = {
    showFilters: false,
    filtered: false,
    buttonsDisabled: false,
    report: [],
    report_format: false,
    monthCount: 0,
    // align with Trial Balance filter shape
    showSpecificDates: false,
    company: [],
    location: [],
    request: {
      fromDate: null,
      toDate: null,
      company_id: '',
      location_id: ''
    }
  };
  // header buttons config similar to Trial Balance
  buttonConfig: any = {
    reportFormat: false,
    // show only one print button; use printReport to match other reports
    print: false,
    printReport: true,
    email: true,
    update: true,
    exportExcel: true,
    close: true,
  };
  @ViewChild('tableRef') tableRef?: ElementRef<HTMLTableElement>;
  @ViewChild('printArea') printArea?: ElementRef<HTMLElement>;

  // Email composition payload for the shared email modal
  reportEmail: EmailModel = new EmailModel();

  constructor(
    private title: Title,
    private reportsApi: FixedAssetReportsApiService,
    private dataExport: DataExportService,
    private notify: NotificationService,
    private cdr: ChangeDetectorRef,
    private dateFilter: DateFilterService,
    private tableDataService: DataService,
    private reportPrint: ReportPrintService,
  ) {
  }

  ngOnInit(): void {
    this.title.setTitle($localize`:Asset Register@@assetRegister:Asset Register`);
    // initialize from/to date lookups similar to Trial Balance
    try {
      const ret = this.dateFilter.DateFilterInit(this.vm.request.toDate, this.vm.request.fromDate);
      (this.vm as any).fromDateSelect = ret.fromDateSelect;
      (this.vm as any).toDateSelect = ret.toDateSelect;
      this.vm.monthCount = ret.monthCount;
    } catch {
    }
    // Populate Company and Location lookups
    try {
      this.tableDataService.getData('company?state=active&pageNumber=0&pageSize=999999')
        .subscribe((result: ResponseModel) => {
          (this.vm as any).company = (result as any)?.Data ?? result;
        });
      this.tableDataService.getData('location?state=active&pageNumber=0&pageSize=999999')
        .subscribe((result: ResponseModel) => {
          (this.vm as any).location = (result as any)?.Data ?? result;
        });
    } catch {
    }
    this.fetchRegister();
  }

  // header buttons - align behavior with Trial Balance
  update(): void {
    this.vm.filtered = true;
    this.fetchRegister();
  }

  clear(): void {
    this.vm.filtered = false;
    this.vm.request = {fromDate: null, toDate: null, company_id: '', location_id: ''};
    this.fetchRegister();
  }

  close(): void {
    // no-op placeholder (header requires handler); page is navigated via app controls
  }

  onPrint(): void {
    const reportName = $localize`:Asset Register@@assetRegister:Asset Register`;
    const rawHtml = this.printArea?.nativeElement?.outerHTML || this.tableRef?.nativeElement?.outerHTML || '';
    const content = this.reportPrint.cleanContentHtml(rawHtml);
    const html = this.reportPrint.buildHtml({title: reportName, contentHtml: content, orientation: 'landscape'});
    this.reportPrint.printInIframe(html);
  }

  onExport(): void {
    const el = this.tableRef?.nativeElement;
    if (el) {
      this.dataExport.exportToExcel(el, 'asset-register.xlsx', 'Asset Register');
    } else {
      this.notify.bsWarning($localize`:Nothing to export@@nothingToExport:Nothing to export`);
    }
  }

  onEmail(): void {
    // Build EmailModel and open the shared email modal
    const emailDocObj = new EmailModel();
    const reportName = $localize`:Asset Register@@assetRegister:Asset Register`;

    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = '';
    emailDocObj.toEmail = '';
    emailDocObj.body = $localize`:EmailBody@@emailBody:Please see the attached ${reportName}.` +
      '<br><br><br>Best regards,<br><br>AcctXErp<br><br>';
    emailDocObj.Subject = 'AcctXErp ' + reportName;

    // ensure latest view before capturing table HTML
    try {
      this.cdr.detectChanges();
    } catch {
    }

    try {
      const html = this.printArea?.nativeElement?.outerHTML || this.tableRef?.nativeElement?.outerHTML || '';
      const content = this.reportPrint.cleanContentHtml(html);
      emailDocObj.pdfHtml = this.reportPrint.buildHtml({title: reportName, contentHtml: content, orientation: 'landscape'});
    } catch {
      emailDocObj.pdfHtml = '';
    }

    const now = new Date();
    const yyyy = String(now.getFullYear());
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    emailDocObj.FileName = `Asset_Register_${yyyy}${mm}${dd}.pdf`;
    emailDocObj.activity_message = reportName;

    this.reportEmail = emailDocObj;

    // open modal
    const element = document.getElementById('sendEmailModal') as HTMLElement;
    if (element) {
      const emailModal = new Modal(element);
      // Re-enable header buttons when the email modal closes
      try {
        element.addEventListener('hidden.bs.modal', () => {
          this.vm.buttonsDisabled = false;
          this.vm.report = this.list && this.list.length ? this.list : [{}];
        }, {once: true});
      } catch {
      }
      emailModal.show();
    }
  }

  // Optional close handler if Close is used in header (safe no-op)
  onClose(): void {
  }

  private fetchRegister(): void {
    const page = 1;
    const pageSize = 100000; // large page to mimic current no-paging UI
    const f: RegisterFilters = {
      fromDate: this.vm.request.fromDate,
      toDate: this.vm.request.toDate,
      companyId: this.vm.request.company_id,
      locationId: this.vm.request.location_id,
      search: undefined,
    };
    this.reportsApi.getRegister(page, pageSize, f).subscribe({
      next: ({items}) => {
        // Map API rows to the fields used by this view
        this.list = (items || []).map(r => ({
          id: r.id,
          name: r.name,
          category: r.category,
          companyName: (r as any).companyName,
          locationName: (r as any).locationName,
          entityName: (r as any).entityName,
          acquisitionDate: r.acquisitionDate,
          acquisitionCost: r.acquisitionCost,
          accumulatedDepreciation: r.accumulatedDepreciation,
          // Use API-supplied MACRS accumulated when available
          accumMacrsAsOfPost: (r as any).macrsAccumulated ?? null,
          netBookValue: r.netBookValue,
          active: r.active,
        } as any));
        this.vm.report = this.list && this.list.length ? this.list : [{}];
        try {
          this.cdr.detectChanges();
        } catch {
        }
      },
      error: (err) => {
        try {
          console.log('[Asset Register][fetch error]', err);
        } catch {
        }
        this.list = [];
        this.vm.report = [];
        try {
          this.cdr.detectChanges();
        } catch {
        }
      }
    });
  }

}
